import itemsAtom from "@/atoms/items";
import optionGroupsAtom from "@/atoms/option-groups";
import { calcMinGroupPrice } from "@/lib/calc-min-group-price";
import { findCategoryForOffer } from "@/lib/find-category-for-offer";
import type { Category, ItemOffer } from "@/types";
import { atom, type Atom } from "jotai";

interface ItemOfferDetailSources {
  categoriesAtom: Atom<Category[]>;
  itemOffersAtom: Atom<ItemOffer[]>;
}

/**
 * Builds the derived atoms for an item offer detail view (modal),
 * parameterized by the category/item-offer source atoms so the same logic
 * serves both contexts (delivery: categoriesAtom/itemOffersAtom; indoor:
 * indoorCategoriesAtom/indoorItemOffersAtom).
 *
 * Consumers must call this inside a useMemo keyed by itemOfferId.
 */
export function makeItemOfferDetailAtoms(
  itemOfferId: string,
  sources: ItemOfferDetailSources,
) {
  const categoryAtom = atom(
    (get) => findCategoryForOffer(get(sources.categoriesAtom), itemOfferId) ?? null,
  );

  const itemOfferAtom = atom(
    (get) => get(sources.itemOffersAtom).find((i) => i.id === itemOfferId) ?? null,
  );

  const itemOfferOptionGroupsAtom = atom((get) => {
    const itemOffer = get(itemOfferAtom);
    if (!itemOffer || !itemOffer.optionGroupsId) return [];
    const optionGroupsId = itemOffer.optionGroupsId;
    const filteredGroups = get(optionGroupsAtom).filter(
      (opg) => opg && opg.id && optionGroupsId.includes(opg.id) && opg.status === "AVAILABLE",
    );

    const sortedGroups = filteredGroups.sort((a, b) => a.index - b.index);

    return sortedGroups.map((group) => ({
      ...group,
      options: [...group.options].sort((a, b) => a.index - b.index),
    }));
  });

  const itemAtom = atom((get) => {
    const itemOffer = get(itemOfferAtom);
    if (!itemOffer) return null;

    return (
      get(itemsAtom).find((i) => {
        return i.id === itemOffer.itemId;
      }) ?? null
    );
  });

  const minimumOfferPriceAtom = atom((get) => {
    const itemOffer = get(itemOfferAtom);
    const optionGroups = get(itemOfferOptionGroupsAtom);
    if (!itemOffer) return null;
    if (!optionGroups.length) return itemOffer.price.value;

    const minPrice = optionGroups.reduce(
      (acc, opg) => acc + calcMinGroupPrice(opg, "value"),
      0,
    );

    return minPrice + itemOffer.price.value;
  });

  return {
    categoryAtom,
    itemOfferAtom,
    itemOfferOptionGroupsAtom,
    itemAtom,
    minimumOfferPriceAtom,
  };
}
